import { useCallback, useEffect, useMemo } from 'react';
import { OnboardingProTask } from '@weave/schema-gen-ts/dist/shared/call-intelligence/plg/onboarding.pb';
import { CallIntelTypes } from '@frontend/api-call-intel';
import { CallInsightApi, OpenOptions } from '@frontend/call-insight-panel';
import { DemoContactPanelComponent } from '@frontend/contact-panel';
import { useDemoPanelShallowStore } from '@frontend/demo-panels';
import { WIDTH_DIMS } from '@frontend/panel-engine';
import { useOnboardingShallowStore } from '@frontend/product-onboarding';
import { useAppScopeStore } from '@frontend/scope';
import { useContactPanelShallowStore } from '@frontend/shared';
import { useSlidePanelShallowStore } from '@frontend/slide-panel';
import { CALL_INTEL_PRO_ONBOARDING_HOTSPOT_KEYS, CI_PRO_ONBOARDING } from '../constant';
import {
  findFirstProEligibleAnalyzedCall,
  findFirstProEligiblePatientContextCall,
  findProEligibleCallTargets,
} from '../utils/call-eligibility';
import { getItemPageNumber } from '../utils/pagination-utils';
import { usePaginatedItemSearch } from './use-paginated-item-search';
import { useProOnboardingActionShallowStore } from './use-pro-onboarding-action-store';

type CallRecordPage = {
  rows?: CallIntelTypes.CallRecord[];
};

type PaginationConfig = {
  currentPage: number;
  setPageNumber: (page: number) => void;
};

type UseProOnboardingCallRecordsTaskResolverArgs = {
  enabled?: boolean;
  allPages?: CallRecordPage[];
  hasNextPage?: boolean;
  isFetching: boolean;
  isFetchingNextPage: boolean;
  fetchNextPage: () => void;
  paginationConfig?: PaginationConfig;
  openCallInsightPanel?: CallInsightApi['open'];
};

const PATIENT_CONTEXT_PAGE_LIMIT = 3;

/**
 * Resolves PRO onboarding call-record based tasks.
 *
 * Task 3: View Call Insights
 * - Use All Call Recordings page 1 only.
 * - Pick first eligible analyzed call.
 *
 * Task 4: Conversation Analysis
 * - Use All Call Recordings page 1 only.
 * - Open Call Insight panel with Call Breakdown accordion.
 *
 * Task 6: Patient-level Data
 * - Search first 3 pages only.
 * - Requires analyzed call + saved contact.
 * - Falls back to demo patient profile if no real match exists.
 */
export function useProOnboardingCallRecordsTaskResolver({
  enabled = true,
  allPages,
  hasNextPage,
  isFetching,
  isFetchingNextPage,
  fetchNextPage,
  paginationConfig,
  openCallInsightPanel,
}: UseProOnboardingCallRecordsTaskResolverArgs) {
  const { flows, completeHotspot, completedHotspots } = useOnboardingShallowStore(
    'flows',
    'completeHotspot',
    'completedHotspots'
  );

  const { pendingTask, clearPendingTask, setEligibility } = useProOnboardingActionShallowStore(
    'pendingTask',
    'clearPendingTask',
    'setEligibility'
  );

  const { selectedLocationIds } = useAppScopeStore();
  const { setPersonId } = useContactPanelShallowStore('setPersonId');
  const { setShow } = useSlidePanelShallowStore('setShow');
  const { register: registerInDemoPanel } = useDemoPanelShallowStore('register');

  const flowRegistered = useMemo(() => flows.some((flow) => flow.id === CI_PRO_ONBOARDING.id), [flows]);

  const shouldRun = enabled && flowRegistered;
  const hasResolvedCallRecords = Array.isArray(allPages);
  const hasLoadedFirstPage = !!allPages?.[0];
  const allSearchPages = useMemo(
    () =>
      allPages?.map((page) => ({
        data: page.rows ?? [],
      })),
    [allPages]
  );

  const { firstPageEligibleAnalyzedCall, firstEligiblePatientAnalyzedCall, currentPageEligibleAnalyzedCall } =
    useMemo(() => {
      const firstPageCalls = allPages?.[0]?.rows ?? [];

      const currentPageIndex = (paginationConfig?.currentPage ?? 1) - 1;
      const currentPageCalls = allPages?.[currentPageIndex]?.rows ?? [];

      const firstPageMatches = findProEligibleCallTargets(firstPageCalls);

      return {
        firstPageEligibleAnalyzedCall: firstPageMatches.firstEligibleAnalyzedCall,
        firstEligiblePatientAnalyzedCall: firstPageMatches.firstEligiblePatientAnalyzedCall,
        currentPageEligibleAnalyzedCall:
          currentPageIndex === 0
            ? firstPageMatches.firstEligibleAnalyzedCall
            : findFirstProEligibleAnalyzedCall(currentPageCalls),
      };
    }, [allPages, paginationConfig?.currentPage]);

  /**
   * Task 6 must inspect first 3 pages only.
   */
  const patientContextPages = useMemo(() => allSearchPages?.slice(0, PATIENT_CONTEXT_PAGE_LIMIT), [allSearchPages]);
  const canFetchMorePatientContextPages =
    !!hasNextPage && (patientContextPages?.length ?? 0) < PATIENT_CONTEXT_PAGE_LIMIT;

  const patientContextCallSearch = usePaginatedItemSearch({
    allPages: patientContextPages,
    isEnabled: shouldRun && hasLoadedFirstPage && pendingTask === OnboardingProTask.PRO_TASK_VIEW_PATIENT_CONTEXT,
    hasNextPage: canFetchMorePatientContextPages,
    isFetching,
    isFetchingNextPage,
    fetchNextPage,
    maxLookaheadPages: PATIENT_CONTEXT_PAGE_LIMIT - 1,
    findItem: findFirstProEligiblePatientContextCall,
  });

  const canShowCallInsightHotspot =
    shouldRun && !completedHotspots.has(CALL_INTEL_PRO_ONBOARDING_HOTSPOT_KEYS.CALL_INSIGHT_BUTTON);

  const eligibleCallInsightHotspotCallId = canShowCallInsightHotspot ? currentPageEligibleAnalyzedCall?.id : undefined;

  const completeHotspotIfNeeded = useCallback(
    (hotspotKey: string) => {
      if (!completedHotspots.has(hotspotKey)) {
        completeHotspot(hotspotKey);
      }
    },
    [completedHotspots, completeHotspot]
  );

  const openDemoPatientProfile = useCallback(() => {
    const demoLocationId = selectedLocationIds[0];

    if (!demoLocationId) return;

    registerInDemoPanel({
      id: 'demo-contact-panel',
      component: DemoContactPanelComponent,
      componentProps: {
        demoLocationId,
        demoFeatures: ['patient-insights', 'call-intel-insights'],
      },
      width: WIDTH_DIMS.medium,
    });
  }, [selectedLocationIds, registerInDemoPanel]);

  const openPatientProfile = useCallback(
    (call: CallIntelTypes.CallRecord) => {
      const personId = call.person?.id;
      if (!personId) return;

      setPersonId(personId, undefined, call.locationId);
      setShow(true, 'contact');
    },
    [setPersonId, setShow]
  );

  const openCallInsightPanelForTask = useCallback(
    (call: CallIntelTypes.CallRecord, options: OpenOptions) => {
      openCallInsightPanel?.(
        {
          channelId: call.id ?? '',
          includeCallIntelligence: false,
          callIntelData: call,
          locationId: call.locationId ?? '',
        },
        {
          defaultAccordion: options.defaultAccordion,
        }
      );
    },
    [openCallInsightPanel]
  );

  /**
   * Resolve Task 3/4 arrow eligibility from All Call Recordings page 1.
   * This runs before checklist click, but only while PRO flow is registered and this resolver is enabled.
   */
  useEffect(() => {
    if (!shouldRun) return;
    if (!hasResolvedCallRecords) return;
    if (isFetching) return;

    setEligibility({
      hasEligibleCallInsightPanel: !!firstPageEligibleAnalyzedCall,
    });
  }, [shouldRun, isFetching, hasResolvedCallRecords, firstPageEligibleAnalyzedCall, setEligibility]);

  useEffect(() => {
    if (!shouldRun) return;
    if (!pendingTask) return;
    if (!hasResolvedCallRecords) return;
    if (isFetching) return;

    switch (pendingTask) {
      case OnboardingProTask.PRO_TASK_VIEW_CALL_INSIGHTS: {
        /**
         * Task 3 uses All Call Recordings page 1 only.
         * If desktop user is currently on another page, move to page 1 first.
         */
        if (paginationConfig && paginationConfig.currentPage !== 1) {
          paginationConfig.setPageNumber(1);
          return;
        }

        if (!firstPageEligibleAnalyzedCall) {
          setEligibility({
            hasEligibleCallInsightPanel: false,
          });

          clearPendingTask();
          return;
        }

        setEligibility({
          hasEligibleCallInsightPanel: true,
        });

        openCallInsightPanelForTask(firstPageEligibleAnalyzedCall, {
          defaultAccordion: 'callIntel',
        });
        completeHotspotIfNeeded(CALL_INTEL_PRO_ONBOARDING_HOTSPOT_KEYS.CALL_INSIGHT_BUTTON);
        clearPendingTask();

        return;
      }

      case OnboardingProTask.PRO_TASK_REVIEW_CONVERSATION_ANALYSIS: {
        /**
         * Task 4 uses same page-1 eligible call criteria as Task 3.
         */
        if (paginationConfig && paginationConfig.currentPage !== 1) {
          paginationConfig.setPageNumber(1);
          return;
        }

        if (!firstEligiblePatientAnalyzedCall) {
          setEligibility({
            hasEligibleCallInsightPanel: false,
          });

          clearPendingTask();
          return;
        }

        setEligibility({
          hasEligibleCallInsightPanel: true,
        });
        completeHotspotIfNeeded(CALL_INTEL_PRO_ONBOARDING_HOTSPOT_KEYS.CALL_INSIGHT_PANEL_CALL_BREAKDOWN);
        openCallInsightPanelForTask(firstEligiblePatientAnalyzedCall, {
          defaultAccordion: 'aboutThisCall',
        });
        clearPendingTask();
        return;
      }

      case OnboardingProTask.PRO_TASK_VIEW_PATIENT_CONTEXT: {
        if (!hasLoadedFirstPage) {
          return;
        }

        const bestCallWithContact = patientContextCallSearch.item;

        /**
         * Task 6 searches first 3 pages only.
         */
        if (!bestCallWithContact) {
          if (patientContextCallSearch.searchExhausted) {
            setEligibility({
              hasEligiblePatientContextCall: false,
            });

            completeHotspotIfNeeded(CALL_INTEL_PRO_ONBOARDING_HOTSPOT_KEYS.PATIENT_CONTACT_AVATAR);
            openDemoPatientProfile();

            clearPendingTask();
          }

          return;
        }

        setEligibility({
          hasEligiblePatientContextCall: true,
        });

        const targetPage = getItemPageNumber(bestCallWithContact, patientContextPages);

        if (paginationConfig && targetPage !== -1 && targetPage !== paginationConfig.currentPage) {
          paginationConfig.setPageNumber(targetPage);
          return;
        }

        completeHotspotIfNeeded(CALL_INTEL_PRO_ONBOARDING_HOTSPOT_KEYS.PATIENT_CONTACT_AVATAR);
        openPatientProfile(bestCallWithContact);
        clearPendingTask();
        return;
      }

      default:
        return;
    }
  }, [
    shouldRun,
    pendingTask,
    isFetching,
    paginationConfig,
    hasResolvedCallRecords,
    firstPageEligibleAnalyzedCall,
    firstEligiblePatientAnalyzedCall,
    setEligibility,
    clearPendingTask,
    openCallInsightPanelForTask,
    completeHotspotIfNeeded,
    hasLoadedFirstPage,
    patientContextCallSearch.item,
    patientContextCallSearch.searchExhausted,
    patientContextPages,
    openDemoPatientProfile,
    openPatientProfile,
  ]);

  return {
    eligibleCallInsightHotspotCallId,
  };
}
